import { useMemo, useState } from 'react';
import type { CanalBot, DimensaoRecorte, RecorteLinha, RecortesBot } from '@/services/api/analytics-bot';
import { comparaNulo, div, n, pct, usd, usd4 } from '../format';
import Avisos from './Avisos';
import EstadoBloco from './EstadoBloco';

type ChaveOrd = 'enviados' | 'entregues' | 'taxa_entrega_pct' | 'cliques' | 'ctr_pct' | 'receita' | 'rpc';

const COLS: [ChaveOrd, string][] = [
    ['enviados', 'Enviados'], ['entregues', 'Entregues'], ['taxa_entrega_pct', 'Entrega'], ['cliques', 'Cliques'],
    ['ctr_pct', 'CTR'], ['receita', 'Receita'], ['rpc', 'RPC'],
];

const DIM: Record<DimensaoRecorte, string> = { pagina: 'Página', pais: 'País', dominio: 'Domínio' };

interface RecortesProps {
    dados: RecortesBot | undefined;
    rotulo: string;
    dimensao: DimensaoRecorte;
    canal: CanalBot;
    onDimensao: (d: DimensaoRecorte) => void;
    onCanal: (c: CanalBot) => void;
    /** O mesmo filtro de país do topo, repetido aqui para não precisar subir a tela. */
    paises: { codigo: string; nome: string }[];
    pais: string | null;
    onPais: (v: string | null) => void;
    carregando: boolean;
    atualizando: boolean;
    erro: boolean;
    onTentar: () => void;
}

const celula = (x: RecorteLinha, k: ChaveOrd): string => {
    switch (k) {
        case 'taxa_entrega_pct': return pct(x.taxa_entrega_pct, 1);
        case 'ctr_pct': return pct(x.ctr_pct);
        case 'receita': return usd(x.receita);
        case 'rpc': return usd4(x.rpc);
        default: return n(x[k]);
    }
};

/** Uma tabela, dois seletores (canal e dimensão); as colunas são sempre as mesmas. */
export default function Recortes({
    dados, rotulo, dimensao, canal, onDimensao, onCanal, paises, pais, onPais, carregando, atualizando, erro, onTentar,
}: RecortesProps) {
    const [ord, setOrd] = useState<{ c: ChaveOrd; desc: boolean }>({ c: 'receita', desc: true });
    const ordenar = (c: ChaveOrd) => setOrd(o => ({ c, desc: o.c === c ? !o.desc : true }));

    const linhas = useMemo(() => {
        const a = [...(dados?.linhas ?? [])];
        a.sort((x, y) => comparaNulo(x[ord.c], y[ord.c], ord.desc));
        return a;
    }, [dados, ord]);
    const maxOrd = Math.max(...linhas.map(x => x[ord.c] ?? 0), 0);

    return (
        <div className="card">
            <div className="card-h">
                <div>
                    <div className="card-t">Recortes · {DIM[dimensao].toLowerCase()} · {rotulo}</div>
                    <div className="card-s">Escolha o canal e a dimensão. As colunas são sempre as mesmas.</div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span className="bar-l">País</span>
                    <select value={pais ?? ''} aria-label="Filtrar recortes por país" onChange={e => onPais(e.target.value || null)}>
                        <option value="">Todos</option>
                        {paises.map(p => <option key={p.codigo} value={p.codigo}>{p.nome}</option>)}
                    </select>
                    <div className="seg">
                        {([['ambos', 'Os dois'], ['flow', 'Só flow'], ['broadcast', 'Só broadcast']] as const).map(([k, l]) => (
                            <button key={k} type="button" data-on={canal === k ? 1 : 0} onClick={() => onCanal(k)}>{l}</button>
                        ))}
                    </div>
                    <div className="seg">
                        {([['pagina', 'Por página'], ['pais', 'Por país'], ['dominio', 'Por domínio']] as const).map(([k, l]) => (
                            <button key={k} type="button" data-on={dimensao === k ? 1 : 0} onClick={() => onDimensao(k)}>{l}</button>
                        ))}
                    </div>
                </div>
            </div>
            {!dados ? (
                <EstadoBloco carregando={carregando} erro={erro} onTentar={onTentar} />
            ) : linhas.length === 0 ? (
                <div className="empty">Nada para mostrar com esse recorte de filtros.</div>
            ) : (
                <div className="tblwrap" data-loading={atualizando ? 1 : 0}>
                    <table>
                        <thead>
                            <tr>
                                <th>{DIM[dimensao]}</th>
                                {COLS.map(([k, l]) => (
                                    <th key={k} className={k === 'receita' ? 'rv' : undefined} data-s={ord.c === k ? 1 : 0} onClick={() => ordenar(k)}>
                                        {l}{ord.c === k ? (ord.desc ? ' ↓' : ' ↑') : ''}
                                    </th>
                                ))}
                                {canal === 'ambos' && <th style={{ textAlign: 'left', cursor: 'default' }}>Flow / Broadcast</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {linhas.map(x => {
                                const somaC = (x.receita_flow ?? 0) + (x.receita_broadcast ?? 0);
                                const fF = div(x.receita_flow, somaC);
                                const fB = div(x.receita_broadcast, somaC);
                                const frac = div(x[ord.c], maxOrd);
                                const baixa = typeof x.taxa_entrega_pct === 'number' && x.taxa_entrega_pct < 80;
                                return (
                                    <tr key={x.chave || x.nome}>
                                        <td style={{ minWidth: 200 }}>
                                            <div style={{ fontWeight: 550 }}>{x.nome}</div>
                                            <div className="minibar">
                                                <i style={{ width: `${(frac ?? 0) * 100}%`, background: ord.c === 'receita' ? 'var(--amber)' : 'var(--green)' }} />
                                            </div>
                                        </td>
                                        {COLS.map(([k]) => (
                                            <td key={k}
                                                className={k === 'receita' ? 'num rv' : k === 'rpc' ? 'num muted' : 'num'}
                                                style={k === 'taxa_entrega_pct' && baixa ? { color: 'var(--red)' }
                                                    : k === 'ctr_pct' ? { color: 'var(--violet)' }
                                                        : k === 'cliques' ? { fontWeight: 620 } : undefined}>
                                                {celula(x, k)}
                                            </td>
                                        ))}
                                        {canal === 'ambos' && (
                                            <td style={{ textAlign: 'left' }}>
                                                <div className="stack">
                                                    {fF !== null && <i style={{ width: `${fF * 100}%`, background: 'var(--teal)', display: 'block' }} />}
                                                    {fB !== null && <i style={{ width: `${fB * 100}%`, background: 'var(--green)', display: 'block' }} />}
                                                </div>
                                                <div className="code" style={{ marginTop: 3 }}>
                                                    {fF === null ? 'sem receita' : `${pct(fF * 100, 0)} flow · ${pct((fB ?? 0) * 100, 0)} broad`}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            <Avisos avisos={dados?.avisos} />
        </div>
    );
}
